import {
  HITLAB_RADIO_ROUTE,
  RADIO_QUEUE_KINDS,
} from './hitLabRadioTracks';

const cleanText = (value, max = 256) =>
  String(value || '').trim().slice(0, max);

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizePlayCount = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count < 0) return 0;
  return Math.floor(count);
};

export const QUALIFIED_PLAY_SIGNAL_DISPLAY_THRESHOLD = 3;

const getMetricPlayCount = (item = {}) =>
  normalizePlayCount(item?.qualifiedPlaySignalCount ?? item?.playCount);

const normalizeSupportReason = (value) => cleanText(value, 180);

const hasSupportFields = (item = {}) =>
  item.hasJgkSupportFields === true ||
  (item.hasJgkSupportFields !== false &&
    (item.canTipWithJgk === true ||
      item.canBuyWithJgk === true ||
      item.canBuySupportWithJgk === true ||
      item.alreadyPurchased === true ||
      Boolean(cleanText(item.tipUnavailableReason, 180)) ||
      Boolean(cleanText(item.buyUnavailableReason, 180))));

const normalizeBuyPrice = (value) => {
  const price = Number(value);
  if (!Number.isFinite(price) || price <= 0) return null;
  return Math.round(price);
};

const getMetricRows = (metrics = null) => {
  if (Array.isArray(metrics)) return metrics;
  if (Array.isArray(metrics?.tracks)) return metrics.tracks;
  if (Array.isArray(metrics?.data?.tracks)) return metrics.data.tracks;
  if (Array.isArray(metrics?.data?.data?.tracks)) {
    return metrics.data.data.tracks;
  }
  return [];
};

export const normalizeRadioMetrics = (metrics = null) =>
  getMetricRows(metrics).reduce((acc, item) => {
    const trackId = cleanText(item?.trackId, 120);
    if (trackId) acc[trackId] = getMetricPlayCount(item);
    return acc;
  }, {});

// Missing/malformed data is not a canonical zero. Keep the legacy normalizer's
// public contract; these merges are only for the two live Radio players.
const readRadioPlayCount = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && !value.trim()) return null;
  const count = Number(value);
  return Number.isFinite(count) && count >= 0 ? Math.floor(count) : null;
};

export const mergeCanonicalRadioMetrics = (current, response) => {
  const updates = {};
  getMetricRows(response).forEach((item) => {
    const trackId = cleanText(item?.trackId, 120);
    const count = readRadioPlayCount(item?.qualifiedPlaySignalCount ?? item?.playCount);
    if (trackId && count !== null) updates[trackId] = count;
  });
  return Object.keys(updates).length ? { ...current, ...updates } : current;
};

export const mergeLegacyRadioPlayCount = (current, play) => {
  const trackId = cleanText(play?.trackId, 120);
  const count = readRadioPlayCount(play?.playCount);
  if (!trackId || count === null) return current;
  return { ...current, [trackId]: Math.max(current?.[trackId] || 0, count) };
};

const getQualifiedListenCount = (metricsByTrackId, trackId) => {
  if (!metricsByTrackId) return null;
  if (!trackId) return null;
  if (!Object.prototype.hasOwnProperty.call(metricsByTrackId, trackId)) {
    return 0;
  }
  return normalizePlayCount(metricsByTrackId[trackId]);
};

export const buildRadioRecordUrl = (capsule) => {
  const trackId = cleanText(capsule?.trackId, 120);
  if (!trackId || capsule?.kind !== RADIO_QUEUE_KINDS.TRACK) {
    return HITLAB_RADIO_ROUTE;
  }
  return `${HITLAB_RADIO_ROUTE}?track=${encodeURIComponent(trackId)}`;
};

export const buildRadioRecordCapsule = (item, metricsByTrackId = null) => {
  const kind = cleanText(item?.kind, 40);
  const isTrack = kind === RADIO_QUEUE_KINDS.TRACK;
  const isBumper = kind === RADIO_QUEUE_KINDS.BUMPER;
  const isDrop = kind === RADIO_QUEUE_KINDS.DROP;
  if (!isTrack && !isBumper && !isDrop) return null;

  const trackId = cleanText(item?.trackId || item?.id, 120);
  const audioUrl = cleanText(item?.audioUrl, 2048);
  if (!trackId) return null;

  const title = isTrack
    ? cleanText(item?.songTitle || item?.title, 160)
    : cleanText(
        item?.label || item?.publicLabel || item?.title || 'Station sweep',
        160,
      );
  const artistName = isTrack
    ? cleanText(item?.artistName, 120)
    : isDrop
      ? 'JG Radio Listener'
      : cleanText(item?.title || 'Joe Galaxy Radio', 120);
  const canGiveFeedback = isTrack && item?.feedbackEligible !== false;
  const qualifiedPlaySignalCount = isTrack
    ? getQualifiedListenCount(metricsByTrackId, trackId)
    : null;
  const displayQualifiedPlaySignal =
    Number(qualifiedPlaySignalCount) >= QUALIFIED_PLAY_SIGNAL_DISPLAY_THRESHOLD;

  return {
    trackId,
    kind,
    dropType: isDrop ? cleanText(item?.dropType || 'listener_drop', 40) : '',
    title,
    artistName,
    linkedArtistPlayerId: isTrack
      ? cleanText(item?.linkedArtistPlayerId || item?.artistPlayerId, 120)
      : '',
    coverArtUrl: isTrack ? cleanText(item?.coverArtUrl, 2048) : '',
    audioUrl,
    durationSeconds: Math.max(0, Math.round(toNumber(item?.durationSeconds))),

    isInRotation: item?.rotationStatus ? item.rotationStatus === 'active' : true,
    isNewInRotation: isTrack && item?.isNewInRotation === true,
    newInRotationUntil: isTrack ? cleanText(item?.newInRotationUntil, 40) : '',

    qualifiedListenCount: qualifiedPlaySignalCount,
    qualifiedPlaySignalCount,
    displayQualifiedPlaySignal,
    qualifiedPlaySignalThreshold: QUALIFIED_PLAY_SIGNAL_DISPLAY_THRESHOLD,

    canPlay: Boolean(audioUrl),

    canGiveFeedback,
    feedbackEarnsJgk: canGiveFeedback,

    hasJgkSupportFields: isTrack && hasSupportFields(item),

    canTipWithJgk: isTrack && item?.canTipWithJgk === true,
    tipUnavailableReason: normalizeSupportReason(item?.tipUnavailableReason),
    tipComingNext: isTrack && !hasSupportFields(item),

    canBuyWithJgk: isTrack && item?.canBuyWithJgk === true,
    canBuySupportWithJgk:
      isTrack &&
      (item?.canBuySupportWithJgk === true || item?.canBuyWithJgk === true),
    buyPriceJgk: normalizeBuyPrice(item?.buyPriceJgk),
    buyUnavailableReason: normalizeSupportReason(item?.buyUnavailableReason),
    alreadyPurchased: isTrack && item?.alreadyPurchased === true,
    downloadAvailable: isTrack && item?.downloadAvailable === true,
    buySupportComingNext: isTrack && !hasSupportFields(item),

    canShare: isTrack,

    artistOnlineStatusAvailable: false,
    isArtistOnline: null,

    liveListenersAvailable: false,
    liveListenerPreview: null,

    messageArtistAvailable: false,

    listenResultStatus: null,
    streetDropStatus: null,
    cityMomentum: null,
  };
};

export const buildRadioRecordCapsules = (queue = [], metricsByTrackId = null) =>
  (Array.isArray(queue) ? queue : [])
    .map((item) => buildRadioRecordCapsule(item, metricsByTrackId))
    .filter(Boolean);
